import React, { useState, useEffect, useCallback } from 'react';
import { useCamera } from '../../hooks/useCamera';
import { useGeolocation } from '../../hooks/useGeolocation';
import { siteDatabaseAPI } from '../../services/api';
import { toast } from 'react-toastify';

const AttendanceModal = ({ isOpen, onClose, siteId, type, onSuccess }) => {
  const [step, setStep] = useState('location'); // 'location' | 'camera' | 'preview' | 'submitting'
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const {
    location,
    error: locationError,
    loading: locationLoading,
    getCurrentPosition,
    clearLocation,
    isSupported: geoSupported
  } = useGeolocation();

  const {
    videoRef,
    canvasRef,
    isActive: cameraActive,
    error: cameraError,
    photo,
    facingMode,
    startCamera,
    stopCamera,
    capturePhoto,
    toggleFacingMode,
    clearPhoto,
    isSupported: cameraSupported
  } = useCamera();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('location');
      setNotes('');
      clearPhoto();
      clearLocation();
    } else {
      stopCamera();
    }
  }, [isOpen, clearPhoto, clearLocation, stopCamera]);

  // Get location when modal opens
  useEffect(() => {
    if (isOpen && step === 'location' && !location && !locationLoading) {
      getCurrentPosition().catch(() => {
        // Error is handled by the hook
      });
    }
  }, [isOpen, step, location, locationLoading, getCurrentPosition]);

  // Handle location confirmed, start camera
  const handleLocationConfirmed = useCallback(async () => {
    if (!location) {
      toast.error('Location is required');
      return;
    }

    setStep('camera');
    try {
      await startCamera();
    } catch (err) {
      toast.error(err.message);
    }
  }, [location, startCamera]);

  // Handle photo capture
  const handleCapture = useCallback(async () => {
    try {
      await capturePhoto();
      stopCamera();
      setStep('preview');
    } catch (err) {
      toast.error('Failed to capture photo');
    }
  }, [capturePhoto, stopCamera]);

  // Handle retake
  const handleRetake = useCallback(async () => {
    clearPhoto();
    setStep('camera');
    try {
      await startCamera();
    } catch (err) {
      toast.error(err.message);
    }
  }, [clearPhoto, startCamera]);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    if (!location || !photo) {
      toast.error('Location and photo are required');
      return;
    }

    setSubmitting(true);
    setStep('submitting');

    try {
      const formData = new FormData();
      formData.append('photo', photo.blob, 'attendance.jpg');
      formData.append('type', type);
      formData.append('latitude', location.latitude.toString());
      formData.append('longitude', location.longitude.toString());
      if (location.accuracy) {
        formData.append('accuracy', location.accuracy.toString());
      }
      if (notes.trim()) {
        formData.append('notes', notes.trim());
      }

      const response = await siteDatabaseAPI.recordAttendance(siteId, formData);

      toast.success(response.data.message || 'Attendance recorded successfully');
      onSuccess && onSuccess(response.data.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to record attendance');
      setStep('preview');
    } finally {
      setSubmitting(false);
    }
  }, [location, photo, type, notes, siteId, onSuccess, onClose]);

  // Handle close with cleanup
  const handleClose = useCallback(() => {
    stopCamera();
    onClose();
  }, [stopCamera, onClose]);

  // Format location display
  const formatLocation = (loc) => {
    if (!loc) return 'Not available';
    return `${loc.latitude.toFixed(6)}, ${loc.longitude.toFixed(6)}`;
  };

  const typeLabel = type === 'check_in' ? 'Check In' : 'Check Out';
  const typeColor = type === 'check_in' ? 'green' : 'red';

  if (!isOpen) return null;

  // Check device support
  if (!geoSupported || !cameraSupported) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Device Not Supported</h3>
            <p className="text-sm text-gray-600 mb-4">
              {!geoSupported && 'Location services are not supported. '}
              {!cameraSupported && 'Camera access is not supported. '}
              Please use a different device.
            </p>
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className={`text-lg font-semibold text-${typeColor}-600`}>
            Site {typeLabel}
          </h2>
          <button
            onClick={handleClose}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Progress indicator */}
          <div className="flex items-center justify-center space-x-2">
            {['location', 'camera', 'preview'].map((s, i) => (
              <React.Fragment key={s}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === s || (step === 'submitting' && s === 'preview')
                    ? 'bg-blue-600 text-white'
                    : ['camera', 'preview', 'submitting'].includes(step) && s === 'location'
                      ? 'bg-green-600 text-white'
                      : ['preview', 'submitting'].includes(step) && s === 'camera'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                }`}>
                  {['camera', 'preview', 'submitting'].includes(step) && s === 'location' ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : ['preview', 'submitting'].includes(step) && s === 'camera' ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </div>
                {i < 2 && <div className={`w-8 h-0.5 ${
                  (['camera', 'preview', 'submitting'].includes(step) && i === 0) ||
                  (['preview', 'submitting'].includes(step) && i === 1)
                    ? 'bg-green-600'
                    : 'bg-gray-200'
                }`} />}
              </React.Fragment>
            ))}
          </div>

          {/* Step labels */}
          <div className="flex justify-between text-xs text-gray-500 px-2">
            <span>Location</span>
            <span>Photo</span>
            <span>Confirm</span>
          </div>

          {/* Step: Location */}
          {step === 'location' && (
            <div className="text-center py-4">
              {locationLoading ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
                    <svg className="w-8 h-8 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  </div>
                  <p className="text-gray-600">Getting your location...</p>
                  <p className="text-sm text-gray-400 mt-2">Please ensure GPS is enabled</p>
                </>
              ) : locationError ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                    <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <p className="text-red-600 mb-4">{locationError.message}</p>
                  <button
                    onClick={() => getCurrentPosition()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Try Again
                  </button>
                </>
              ) : location ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-900 font-medium mb-1">Location Captured</p>
                  <p className="text-sm text-gray-500 mb-1 font-mono">{formatLocation(location)}</p>
                  {location.accuracy && (
                    <p className="text-xs text-gray-400 mb-4">Accuracy: ~{Math.round(location.accuracy)}m</p>
                  )}
                  <button
                    onClick={handleLocationConfirmed}
                    className={`px-6 py-2 bg-${typeColor}-600 text-white rounded-lg hover:bg-${typeColor}-700`}
                  >
                    Continue to Camera
                  </button>
                </>
              ) : null}
            </div>
          )}

          {/* Step: Camera */}
          {step === 'camera' && (
            <div className="relative">
              {/* Video preview */}
              <div className="relative bg-black rounded-lg overflow-hidden aspect-[4/3]">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />

                {/* Camera controls overlay */}
                <div className="absolute bottom-4 left-0 right-0 flex justify-center items-center gap-6">
                  <button
                    onClick={toggleFacingMode}
                    className="p-3 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors"
                    title="Switch Camera"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                  <button
                    onClick={handleCapture}
                    className="w-16 h-16 bg-white rounded-full border-4 border-gray-300 hover:border-gray-400 transition-colors flex items-center justify-center"
                    title="Capture Photo"
                  >
                    <div className={`w-12 h-12 rounded-full bg-${typeColor}-500`} />
                  </button>
                  <div className="w-12" /> {/* Spacer for balance */}
                </div>

                {/* Facing mode indicator */}
                <div className="absolute top-4 right-4">
                  <span className="px-2 py-1 bg-black/50 text-white text-xs rounded">
                    {facingMode === 'environment' ? 'Back Camera' : 'Front Camera'}
                  </span>
                </div>
              </div>

              {/* Hidden canvas for capture */}
              <canvas ref={canvasRef} className="hidden" />

              {cameraError && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {cameraError.message}
                </div>
              )}
            </div>
          )}

          {/* Step: Preview */}
          {(step === 'preview' || step === 'submitting') && photo && (
            <div>
              {/* Photo preview */}
              <div className="relative bg-black rounded-lg overflow-hidden aspect-[4/3]">
                <img
                  src={photo.dataUrl}
                  alt="Captured attendance"
                  className="w-full h-full object-cover"
                />

                {/* Preview overlay - shows what will be added on backend */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/60 to-transparent p-4">
                  <div className={`text-lg font-bold ${type === 'check_in' ? 'text-green-400' : 'text-red-400'}`}>
                    {type === 'check_in' ? 'CHECK IN' : 'CHECK OUT'}
                  </div>
                  <div className="text-white text-sm">
                    {new Date().toLocaleString('en-MY', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true
                    })}
                  </div>
                  <div className="text-gray-300 text-xs font-mono">
                    GPS: {formatLocation(location)}
                  </div>
                </div>
              </div>

              {/* Notes input */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about this attendance..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={2}
                  disabled={submitting}
                />
              </div>

              {/* Action buttons */}
              <div className="mt-4 flex gap-3">
                <button
                  onClick={handleRetake}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Retake Photo
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className={`flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                    type === 'check_in'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    `Confirm ${typeLabel}`
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceModal;
